import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../environment';
import { initDatadogRum } from './datadog-rum';
import { ReportingEvent } from './events';
import { RouterOutput } from '../trpc';
import { AppSessionData, OrgSessionData } from '@strength/server-auth';
import { initDatadogLogs } from './datadog-logs';
import { athleteDisplayName } from '@strength/domain';

// Lazy load CustomerIO Analytics to avoid loading the SDK in gov/non-production environments
type CioAnalyticsModule = typeof import('./CioAnalytics').default;
let cioAnalyticsPromise: Promise<CioAnalyticsModule> | null = null;
const getCioAnalytics = () => {
  if (!cioAnalyticsPromise) {
    cioAnalyticsPromise = import('./CioAnalytics').then((m) => m.default);
  }
  return cioAnalyticsPromise;
};

// CustomerIO is only enabled in production for non-gov orgs
const isCioEnabled =
  environment.VITE_ENVIRONMENT === 'production' && !environment.VITE_IS_GOV;

export type ReportingUser = {
  axleProfileId: string;
  email: string;
  givenName: string;
  familyName: string;
  isActive: boolean;
  isAdmin: boolean;
  axleOrgId: string;
};
export type OrgDocument = RouterOutput['admin']['axleOrgDocument'];

declare global {
  interface Window {
    [key: string]: any;
  }
}

const gainsightPxTag = `
(function(n,t,a,e,co){var i="aptrinsic";n[i]=n[i]||function(){
    (n[i].q=n[i].q||[]).push(arguments)},n[i].p=e;n[i].c=co;
  var r=t.createElement("script");r.async=!0,r.src=a+"?a="+e;
  var c=t.getElementsByTagName("script")[0];c.parentNode.insertBefore(r,c)
})(window,document,"https://web-sdk.aptrinsic.com/api/aptrinsic.js","${environment.VITE_GAINSIGHT_PX_KEY}");
`;

// Axle integration todo: update IDs per this example: https://www.notion.so/teamworksoss/Teamworks-Gainsight-Implementation-Guide-12e6e70bf842495084e4e11f893cec1f?pvs=4

export const initReporting = async () => {
  // Disable Gainsight PX in gov environments
  if (!environment.VITE_IS_GOV) {
    // Inject Gainsight PX script
    const gainsightPxScript = document.createElement('script');
    gainsightPxScript.textContent = gainsightPxTag;
    document.body.appendChild(gainsightPxScript);
  }

  initDatadogLogs();
  initDatadogRum();

  // Initialize CustomerIO SDK early for in-app messages to work properly
  // This must be called before user identification
  if (isCioEnabled) {
    const CioAnalytics = await getCioAnalytics();
    CioAnalytics.initialize();
  }
};

export const clearReportingUser = async () => {
  // Only call Gainsight if not in gov environment
  if (!environment.VITE_IS_GOV) {
    window['aptrinsic']('reset');
  }
  if (isCioEnabled) {
    const CioAnalytics = await getCioAnalytics();
    await CioAnalytics.resetUser();
  }
  datadogRum.setUser({});
};

export const setReportingUserForProfile = async (
  auth: OrgSessionData,
  orgDoc: OrgDocument,
  reportingUser: ReportingUser,
  teamId: string,
) => {
  const { type: sessionType, userId } = auth;
  const { profileId, orgId } = auth;

  // set the user in Datadog (omit PII in gov environments)
  datadogRum.setUser({
    id: userId,
    type: sessionType,
    profileId,
    orgId,
    teamId,
    ...(!environment.VITE_IS_GOV && {
      orgName: orgDoc?.org.name,
      name: athleteDisplayName(reportingUser),
      email: reportingUser.email,
    }),
  });

  const canTrack =
    environment.VITE_ENVIRONMENT === 'production' &&
    !reportingUser.email?.endsWith('@sink.sendgrid.net') &&
    !reportingUser.email?.endsWith('@qawolf.email');

  if (canTrack) {
    // set the user in Gainsight (disabled in gov environments)
    if (!environment.VITE_IS_GOV) {
      window['aptrinsic'](
        'identify',
        {
          id: reportingUser.axleProfileId,
          strengthUserId: userId,
          email: reportingUser.email,
          firstName: reportingUser.givenName,
          lastName: reportingUser.familyName,
          strengthIsActive: reportingUser.isActive,
          strengthIsAdmin: reportingUser.isAdmin,
        },
        {
          id: reportingUser.axleOrgId,
          name: orgDoc?.org.name,
          strengthAxleOrgId: reportingUser.axleOrgId,
        },
      );
    }

    // set the user in CustomerIO (lazy loaded)
    if (isCioEnabled) {
      const CioAnalytics = await getCioAnalytics();
      await CioAnalytics.setUser(reportingUser, auth, teamId);
    }
  }
};

export const setReportingUserForAdmin = (auth: AppSessionData) => {
  const { type: sessionType, userId } = auth;

  datadogRum.setUser({
    userId,
    type: sessionType,
  });
};

export const trackEvent = <E extends keyof ReportingEvent>(
  eventName: E,
  payload: ReportingEvent[E],
) => {
  // Only call Gainsight if not in gov environment
  if (!environment.VITE_IS_GOV) {
    window['aptrinsic']('track', eventName, payload);
  }
  datadogRum.addAction(eventName, payload);
};
